import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getAxiosInstance } from "../../../api";

const initialState = {
  currentFormDataBuy: {},
  currentFormDataSell: {},
  selectedItemsDetails: "",
  selectedId: "",
  showRateEditModal: false,

  activeTab: "Buy",
  webId: "",
  completedTabs: {
    Buy: false,
    Sale: false,
    Remittance: false,
  },
  CompletedPercentage: {
    Buy: 0,
    Sale: 0,
    Remittance: 0,
  },
};

// NOTE: Only the edit (fetch) and update endpoints below are different from the
// Branch Rate edit. Update the endpoints here once the currency rate APIs are ready.
export const getCurrencyRateDetails = createAsyncThunk(
  "admin/currencies/rate/edit",
  // `rowData` is the selected currency-list row, used as a fallback when the
  // rate API has no saved data yet (see the fulfilled reducer below).
  async ({ id }, { rejectWithValue }) => {
    const api = await getAxiosInstance();
    try {
      const response = await api.get(
        `admin/currencies/rate-margin?currency_id=${id}`
      );

      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Flattens a nested object/array into bracket-notation params
// (e.g. product[0][id], product[0][branch_ids][0]) so the payload is sent as
// application/x-www-form-urlencoded, matching the rate-margin/save contract.
// Skips undefined/null so absent ids aren't sent as the literal string.
const buildFormParams = (data, params = new URLSearchParams(), parentKey = "") => {
  if (data === undefined || data === null) return params;
  if (Array.isArray(data)) {
    data.forEach((value, index) => {
      buildFormParams(value, params, `${parentKey}[${index}]`);
    });
  } else if (typeof data === "object") {
    Object.entries(data).forEach(([key, value]) => {
      buildFormParams(value, params, parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    params.append(parentKey, data);
  }
  return params;
};

export const updateCurrencyRateDetails = createAsyncThunk(
  "/admin/currencies/rate/update",
  async (values, { rejectWithValue }) => {
    const api = await getAxiosInstance();
    try {
      const response = await api.put(
        `/admin/currencies/rate-margin/save`,
        buildFormParams(values),
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );

      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const currencyRateFormSlice = createSlice({
  name: "currencyRateForm",
  initialState,
  reducers: {
    updateFormData: (state, action) => {
      action.payload(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCurrencyRateDetails.fulfilled, (state, action) => {
      const apiData = action.payload?.data?.data;
      const isEmpty =
        apiData == null || (Array.isArray(apiData) && apiData.length === 0);
      // When the rate API returns no saved data, fall back to the selected
      // row data so the edit modal is still pre-filled; otherwise use the API.
      state.selectedItemsDetails = isEmpty
        ? action.meta.arg?.rowData ?? ""
        : apiData;
    });
  },
});

export const { updateFormData } = currencyRateFormSlice.actions;

export default currencyRateFormSlice.reducer;
